// Colour maths for the theme: the user's own theme colour becomes a Mantine palette here, and the
// shell asks here whether white or dark text reads on a colour (WCAG contrast). Only 3- or 6-digit
// hex is accepted anywhere: Mantine turns every other format into black (to-rgba.js), so a broken
// value could never paint the CRM black.

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

const clamp01 = (n) => Math.min(1, Math.max(0, n));

/** "#ABC", "abc", "#aabbcc" -> "#aabbcc"; anything else -> null. */
export function normalizeHex(value) {
  if (typeof value !== "string") return null;
  const match = value.trim().match(HEX_RE);
  if (!match) return null;
  let hex = match[1].toLowerCase();
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }
  return `#${hex}`;
}

export function hexToRgb(value) {
  const hex = normalizeHex(value);
  if (!hex) return null;
  const n = parseInt(hex.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbToHex({ r, g, b }) {
  const part = (c) =>
    Math.round(Math.min(255, Math.max(0, c)))
      .toString(16)
      .padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`;
}

function rgbToHsl({ r, g, b }) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return { h: h / 6, s, l };
}

function hslToRgb({ h, s, l }) {
  if (s === 0) {
    const v = l * 255;
    return { r: v, g: v, b: v };
  }
  const hueToChannel = (p, q, t) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: hueToChannel(p, q, h + 1 / 3) * 255,
    g: hueToChannel(p, q, h) * 255,
    b: hueToChannel(p, q, h - 1 / 3) * 255,
  };
}

/**
 * A Mantine palette (10 shades, light to dark) around one colour. Shade 6 is the picked colour
 * exactly - Mantine's filled shade in light mode - the lighter shades step evenly up to a faint
 * tint and the darker ones down to a deep tone (the sidebar uses 8 and 9). Same hue and
 * saturation throughout, so the palette reads as one colour. Returns null for an invalid hex.
 */
export function buildPalette(value) {
  const base = normalizeHex(value);
  if (!base) return null;
  const { h, s, l } = rgbToHsl(hexToRgb(base));
  const lightest = Math.min(0.985, Math.max(0.96, l + 0.03));
  const darkest = l * 0.4;

  const shades = [];
  for (let i = 0; i < 10; i += 1) {
    if (i === 6) {
      shades.push(base);
    } else {
      const lightness =
        i < 6 ? lightest - (lightest - l) * (i / 6) : l - (l - darkest) * ((i - 6) / 3);
      shades.push(rgbToHex(hslToRgb({ h, s, l: clamp01(lightness) })));
    }
  }
  return shades;
}

/** `from` moved `amount` (0..1) of the way to `to`, in sRGB like the browser's own blending. */
export function mixHex(from, to, amount) {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  if (!a || !b) return normalizeHex(from) || "#000000";
  const t = clamp01(amount);
  return rgbToHex({
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  });
}

const linearChannel = (c) => {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function luminance(value) {
  const rgb = hexToRgb(value);
  if (!rgb) return 0;
  return 0.2126 * linearChannel(rgb.r) + 0.7152 * linearChannel(rgb.g) + 0.0722 * linearChannel(rgb.b);
}

/** WCAG contrast ratio between two colours, 1 to 21 (4.5 = readable body text). */
export function contrastRatio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/** White or the near-black ink, whichever reads better on `background`. */
export function readableOn(background, light = "#ffffff", dark = "#101113") {
  return contrastRatio(light, background) >= contrastRatio(dark, background) ? light : dark;
}
